import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('forgottenPassword.emailRequired', 'Please enter your email address'));
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('forgottenPassword.error', 'Failed to process your request'));
      }
      
      setSuccess(true);
      
    } catch (err) {
      setError(err.message || t('forgottenPassword.serverError', 'Server error. Please try again later.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-md mx-auto pt-16 px-4">
        <div className="bg-white p-8 border rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6 text-center">
            {t('forgottenPassword.title', 'Reset Your Password')}
          </h2>
          
          {!success ? (
            <>
              <p className="mb-6 text-center text-gray-600">
                {t('forgottenPassword.instruction', 'Enter your email address and we will send you a link to reset your password.')}
              </p>
              
              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                    {t('forgottenPassword.email', 'Email Address')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded focus:outline-none transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting 
                    ? t('forgottenPassword.submitting', 'Sending...') 
                    : t('forgottenPassword.submit', 'Send Reset Link')}
                </button>
              </form>
            </>
          ) : (
            <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-md mb-6">
              <p className="font-medium">{t('forgottenPassword.emailSent', 'Reset Link Sent!')}</p>
              <p>{t('forgottenPassword.checkEmail', 
                'If an account exists with this email address, you will receive a password reset link shortly. Please check your inbox and spam folders.')}</p>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <Link to="/login" className="text-blue-600 hover:underline">
              {t('forgottenPassword.backToLogin', 'Back to Login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 